// Mock API layer that simulates backend calls
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// IMPORTANT: Return cloned data to prevent shared-reference mutations between
// the in-memory DB and React state (which can cause "first add" duplicates).
const clone = (value) => {
  // structuredClone is available in modern browsers/node; fallback for older envs.
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
};

const makeId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const nowIso = () => new Date().toISOString();

// Utility function to generate tasks from recurrence patterns
// completedMap: optional object of instanceKey -> true for completed tasks (used by context)
const generateTasksFromRecurrence = (
  taskTemplate,
  recurrencePattern,
  checkCompleted = false,
  completedMap = null
) => {
  const completed = completedMap != null ? completedMap : completedTaskInstances;
  if (!recurrencePattern || !recurrencePattern.isRecurring) {
    // If not recurring, return a single task
    const scheduledDateTime = recurrencePattern?.startDateTime || new Date().toISOString();
    const instanceKey = `${taskTemplate.id}-${scheduledDateTime}`;
    const isCompleted = checkCompleted && completed[instanceKey];
    return [{
      ...taskTemplate,
      id: `${taskTemplate.id}-${scheduledDateTime}`, // Unique ID for instance
      templateId: taskTemplate.id, // Keep reference to template
      dueDate: scheduledDateTime.split('T')[0],
      dueTime: scheduledDateTime.split('T')[1]?.slice(0, 5) || '',
      scheduledDateTime: scheduledDateTime,
      status: isCompleted ? "Completed" : taskTemplate.status || "Pending",
    }];
  }

  const tasks = [];
  const startDate = new Date(recurrencePattern.startDateTime);

  // Calculate end date based on end condition
  let endDate = null;
  if (recurrencePattern.endCondition?.type === 'duration') {
    const duration = recurrencePattern.endCondition.duration;
    const value = duration.value || 0;
    const unit = duration.unit || 'days';

    endDate = new Date(startDate);
    if (unit === 'hours') {
      endDate.setHours(endDate.getHours() + value);
    } else if (unit === 'days') {
      endDate.setDate(endDate.getDate() + value);
    } else if (unit === 'weeks') {
      endDate.setDate(endDate.getDate() + (value * 7));
    }
  } else if (recurrencePattern.endCondition?.type === 'untilDate') {
    endDate = new Date(recurrencePattern.endCondition.untilDateTime);
  } else if (recurrencePattern.endCondition?.type === 'count') {
    // Will be handled in the loop
  }

  const frequency = recurrencePattern.frequency;
  const interval = recurrencePattern.interval || 1;
  const specificTimes = recurrencePattern.specificTimes || [];
  const daysOfWeek = recurrencePattern.daysOfWeek || [];

  let currentDate = new Date(startDate);
  let taskCount = 0;
  const maxTasks = 1000; // Safety limit
  const maxCount = recurrencePattern.endCondition?.type === 'count'
    ? recurrencePattern.endCondition.count
    : null;

  while (taskCount < maxTasks) {
    // Check end conditions
    if (endDate && currentDate > endDate) break;
    if (maxCount !== null && taskCount >= maxCount) break;

    if (frequency === 'hourly') {
      // Generate tasks every N hours
      if (taskCount === 0 || (taskCount % interval === 0)) {
        const scheduledDateTime = new Date(currentDate);
        const instanceKey = `${taskTemplate.id}-${scheduledDateTime.toISOString()}`;
        const isCompleted = checkCompleted && completed[instanceKey];
        tasks.push({
          ...taskTemplate,
          id: `${taskTemplate.id}-${scheduledDateTime.toISOString()}`,
          templateId: taskTemplate.id,
          dueDate: scheduledDateTime.toISOString().split('T')[0],
          dueTime: scheduledDateTime.toTimeString().slice(0, 5),
          scheduledDateTime: scheduledDateTime.toISOString(),
          status: isCompleted ? "Completed" : taskTemplate.status || "Pending",
        });
        taskCount++;
      }
      currentDate.setHours(currentDate.getHours() + interval);
    } else if (frequency === 'daily') {
      // Generate tasks daily at specific times
      if (specificTimes.length > 0) {
        for (const time of specificTimes) {
          const [hours, minutes] = time.split(':').map(Number);
          const scheduledDateTime = new Date(currentDate);
          scheduledDateTime.setHours(hours, minutes, 0, 0);

          if (!endDate || scheduledDateTime <= endDate) {
            const instanceKey = `${taskTemplate.id}-${scheduledDateTime.toISOString()}`;
            const isCompleted = checkCompleted && completed[instanceKey];
            tasks.push({
              ...taskTemplate,
              id: `${taskTemplate.id}-${scheduledDateTime.toISOString()}`, // Unique ID for instance
              templateId: taskTemplate.id, // Keep reference to template
              dueDate: scheduledDateTime.toISOString().split('T')[0],
              dueTime: time,
              scheduledDateTime: scheduledDateTime.toISOString(),
              status: isCompleted ? "Completed" : taskTemplate.status || "Pending",
            });
            taskCount++;
          }
        }
      } else {
        // No specific times, use start time
        const scheduledDateTime = new Date(currentDate);
        const instanceKey = `${taskTemplate.id}-${scheduledDateTime.toISOString()}`;
        const isCompleted = checkCompleted && completed[instanceKey];
        tasks.push({
          ...taskTemplate,
          id: `${taskTemplate.id}-${scheduledDateTime.toISOString()}`, // Unique ID for instance
          templateId: taskTemplate.id, // Keep reference to template
          dueDate: scheduledDateTime.toISOString().split('T')[0],
          dueTime: scheduledDateTime.toTimeString().slice(0, 5),
          scheduledDateTime: scheduledDateTime.toISOString(),
          status: isCompleted ? "Completed" : taskTemplate.status || "Pending",
        });
        taskCount++;
      }
      currentDate.setDate(currentDate.getDate() + interval);
    } else if (frequency === 'weekly') {
      // Generate tasks on specific days of week
      if (daysOfWeek.length > 0) {
        for (const dayName of daysOfWeek) {
          const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(dayName.toLowerCase());
          if (dayIndex === -1) continue;

          const scheduledDateTime = new Date(currentDate);
          const currentDay = scheduledDateTime.getDay();
          const daysToAdd = (dayIndex - currentDay + 7) % 7;
          scheduledDateTime.setDate(scheduledDateTime.getDate() + daysToAdd);

          // Set time from specificTimes or start time
          if (specificTimes.length > 0) {
            const [hours, minutes] = specificTimes[0].split(':').map(Number);
            scheduledDateTime.setHours(hours, minutes, 0, 0);
          }

          if ((!endDate || scheduledDateTime <= endDate) && scheduledDateTime >= startDate) {
            const instanceKey = `${taskTemplate.id}-${scheduledDateTime.toISOString()}`;
            const isCompleted = checkCompleted && completed[instanceKey];
            tasks.push({
              ...taskTemplate,
              id: `${taskTemplate.id}-${scheduledDateTime.toISOString()}`, // Unique ID for instance
              templateId: taskTemplate.id, // Keep reference to template
              dueDate: scheduledDateTime.toISOString().split('T')[0],
              dueTime: scheduledDateTime.toTimeString().slice(0, 5),
              scheduledDateTime: scheduledDateTime.toISOString(),
              status: isCompleted ? "Completed" : taskTemplate.status || "Pending",
            });
            taskCount++;
          }
        }
      }
      currentDate.setDate(currentDate.getDate() + (7 * interval));
    } else if (frequency === 'monthly') {
      // Generate tasks monthly
      const scheduledDateTime = new Date(currentDate);
      if (recurrencePattern.dayOfMonth) {
        scheduledDateTime.setDate(recurrencePattern.dayOfMonth);
      }
      if (specificTimes.length > 0) {
        const [hours, minutes] = specificTimes[0].split(':').map(Number);
        scheduledDateTime.setHours(hours, minutes, 0, 0);
      }

      if ((!endDate || scheduledDateTime <= endDate) && scheduledDateTime >= startDate) {
        const instanceKey = `${taskTemplate.id}-${scheduledDateTime.toISOString()}`;
        const isCompleted = checkCompleted && completed[instanceKey];
        tasks.push({
          ...taskTemplate,
          id: `${taskTemplate.id}-${scheduledDateTime.toISOString()}`, // Unique ID for instance
          templateId: taskTemplate.id, // Keep reference to template
          dueDate: scheduledDateTime.toISOString().split('T')[0],
          dueTime: scheduledDateTime.toTimeString().slice(0, 5),
          scheduledDateTime: scheduledDateTime.toISOString(),
          status: isCompleted ? "Completed" : taskTemplate.status || "Pending",
        });
        taskCount++;
      }
      currentDate.setMonth(currentDate.getMonth() + interval);
    }

    // Safety check to prevent infinite loops
    if (currentDate > new Date('2030-12-31')) break;
  }

  return tasks;
};

const normalizeMedicationCarePlanData = (data) => {
  if (!data) return { medications: [] };

  // Backward compat: older UI submitted a single medication object directly.
  const meds =
    Array.isArray(data?.medications)
      ? data.medications
      : data.drugName
        ? [data]
        : Array.isArray(data)
          ? data
          : [];

  return {
    ...data,
    medications: meds.map((m) => ({
      id: m.id || makeId(),
      drugName: m.drugName || "",
      dose: m.dose || "",
      route: m.route || "",
      frequency: m.frequency || "",
      duration: m.duration || "",
      indication: m.indication || "",
      prnInstructions: m.prnInstructions || "",
      startDate: m.startDate || "",
      endDate: m.endDate || "",
      status: m.status || "active", // active | stopped
      stoppedAt: m.stoppedAt || null,
    })),
  };
};

const normalizeTestCarePlanData = (data) => {
  if (!data) return { tests: [] };

  // Backward compat: older UI submitted a single test object directly.
  const testList =
    Array.isArray(data?.tests)
      ? data.tests
      : data.testName
        ? [data]
        : Array.isArray(data)
          ? data
          : [];

  return {
    ...data,
    tests: testList.map((t) => ({
      id: t.id || makeId(),
      testName: t.testName || "",
      priority: t.priority || "routine",
      clinicalIndication: t.clinicalIndication || "",
      frequency: t.frequency || "",
      scheduledDateTime: t.scheduledDateTime || "",
    })),
  };
};

const normalizeMonitoringCarePlanData = (data) => {
  if (!data) return { monitoringItems: [] };

  // Backward compat: older UI submitted a single monitoring object directly.
  const monitoringList =
    Array.isArray(data?.monitoringItems)
      ? data.monitoringItems
      : data.parameter
        ? [data]
        : Array.isArray(data)
          ? data
          : [];

  return {
    ...data,
    monitoringItems: monitoringList.map((m) => ({
      id: m.id || makeId(),
      parameter: m.parameter || "",
      frequency: m.frequency || "",
      thresholds: m.thresholds || "",
      responsibleRole: m.responsibleRole || "",
      duration: m.duration || "",
    })),
  };
};

const mockAvailableDoctors = [
  { id: "doc1", name: "Dr. Arjun Mehta", specialty: "Endocrinology", initials: "AM" },
  { id: "doc2", name: "Dr. Kavya Nair", specialty: "Internal Medicine", initials: "KN" },
  { id: "doc3", name: "Dr. Rohan Gupta", specialty: "Nephrology", initials: "RG" },
  { id: "doc4", name: "Dr. Priya Sharma", specialty: "Cardiology", initials: "PS" },
  { id: "doc5", name: "Dr. Suresh Patel", specialty: "Neurology", initials: "SP" },
  { id: "doc6", name: "Dr. Meera Reddy", specialty: "Pediatrics", initials: "MR" },
  { id: "doc7", name: "Dr. Vikram Singh", specialty: "General Surgery", initials: "VS" }
];

const mockPatients = [
  {
    id: "1",
    name: "Rajesh Kumar",
    wardNo: "A1",
    roomNo: "101",
    bedNo: "1A",
    type: "Main",
    condition: "Post-operative care after appendectomy",
    diseases: ["Appendicitis"],
    age: 45,
    gender: "Male",
    admissionDate: "2026-01-05",
    vitals: {
      bloodPressure: "120/80",
      heartRate: 72,
      temperature: 98.6,
      oxygenSaturation: 98,
      respiratoryRate: 16,
      weight: 75,
      lastUpdated: "2026-01-09 08:30",
    },
    carePlans: [],
    referredDoctors: [
      { id: "doc2", name: "Dr. Kavya Nair", specialty: "Internal Medicine", initials: "KN", isPrimary: true }
    ],
    tasks: (() => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      // Store only task templates with recurrence patterns
      return [
        {
          id: makeId(),
          taskTitle: "Administer 500mg Amoxicillin with meal",
          description: "Administer 500mg Amoxicillin with meal",
          priority: "High",
          category: "Medicine",
          status: "Pending",
          assignedTo: "Nurse",
          createdAt: new Date().toISOString(),
          recurrencePattern: {
            isRecurring: true,
            frequency: "daily",
            interval: 1,
            specificTimes: ["09:00"],
            daysOfWeek: [],
            dayOfMonth: null,
            startDateTime: `${yesterdayStr}T09:00:00Z`,
            endCondition: {
              type: "duration",
              duration: {
                value: 5,
                unit: "days"
              }
            },
            timezone: "Asia/Kolkata"
          }
        },
        {
          id: makeId(),
          taskTitle: "IV fluid check and adjustment",
          description: "IV fluid check and adjustment",
          priority: "Medium",
          category: "Medicine",
          status: "Pending",
          assignedTo: "Nurse",
          createdAt: new Date().toISOString(),
          recurrencePattern: {
            isRecurring: true,
            frequency: "daily",
            interval: 1,
            specificTimes: ["11:00"],
            daysOfWeek: [],
            dayOfMonth: null,
            startDateTime: `${yesterdayStr}T11:00:00Z`,
            endCondition: {
              type: "duration",
              duration: {
                value: 3,
                unit: "days"
              }
            },
            timezone: "Asia/Kolkata"
          }
        },
        {
          id: makeId(),
          taskTitle: "aaaa",
          description: "cscsc",
          priority: "Medium",
          category: "Medicine",
          status: "Pending",
          assignedTo: "Nurse",
          createdAt: new Date().toISOString(),
          recurrencePattern: {
            isRecurring: false,
            frequency: "daily",
            interval: 1,
            specificTimes: [],
            daysOfWeek: [],
            dayOfMonth: null,
            startDateTime: `${yesterdayStr}T11:00:00Z`,
            endCondition: {
              type: "never",
              duration: {
                value: null,
                unit: "days"
              }
            },
            timezone: "Asia/Kolkata"
          }
        },
      ];
    })(),
  },
  {
    id: "2",
    name: "Priya Sharma",
    wardNo: "A1",
    roomNo: "102",
    bedNo: "2B",
    type: "Referral",
    condition: "Diabetic ketoacidosis management",
    diseases: ["Type 2 Diabetes", "Hypertension"],
    age: 52,
    gender: "Female",
    admissionDate: "2026-01-07",
    vitals: {
      bloodPressure: "140/90",
      heartRate: 88,
      temperature: 99.2,
      oxygenSaturation: 96,
      respiratoryRate: 18,
      weight: 68,
      lastUpdated: "2026-01-09 09:00",
    },
    carePlans: [],
    referredDoctors: [
      { id: "doc1", name: "Dr. Arjun Mehta", specialty: "Endocrinology", initials: "AM", isPrimary: true }
    ],
    tasks: (() => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      return [
        {
          id: makeId(),
          taskTitle: "Blood Glucose Monitoring",
          description: "Check blood glucose levels",
          priority: "Medium",
          category: "Vitals",
          status: "Pending",
          assignedTo: "Nurse",
          createdAt: new Date().toISOString(),
          recurrencePattern: {
            isRecurring: true,
            frequency: "hourly",
            interval: 6,
            specificTimes: [],
            daysOfWeek: [],
            dayOfMonth: null,
            startDateTime: `${yesterdayStr}T06:00:00Z`,
            endCondition: {
              type: "duration",
              duration: {
                value: 3,
                unit: "days"
              }
            },
            timezone: "Asia/Kolkata"
          }
        }
      ];
    })(),
  },
  // {
  //   id: "3",
  //   name: "Reetangsee Dutta",
  //   wardNo: "B2",
  //   roomNo: "201",
  //   bedNo: "GM-12",
  //   type: "Main",
  //   condition: "Pneumonia treatment with IV antibiotics",
  //   diseases: ["Community-acquired Pneumonia", "COPD"],
  //   age: 24,
  //   gender: "Female",
  //   admissionDate: "2026-02-16",
  //   vitals: {
  //     bloodPressure: "130/85",
  //     heartRate: 92,
  //     temperature: 100.4,
  //     oxygenSaturation: 94,
  //     respiratoryRate: 22,
  //     weight: 70,
  //     lastUpdated: "2026-02-21 15:30",
  //   },
  //   carePlans: [{
  //     type: "clinical-notes",
  //     data: {
  //       condition:
  //         "Hypersensitivity pneumonitis (J67) with respiratory distress and hypoxia requiring ICU care",
  //       goals:
  //         "Stabilize respiratory status, reduce lung inflammation, normalize oxygen saturation",
  //       notes:
  //         "Severe shortness of breath, persistent cough, high-grade fever, hypoxia; ICU transfer required",
  //     },
  //     id: "CN-001",
  //     createdAt: "2026-02-16T00:00:00Z",
  //   },
  //   {
  //     type: "monitoring",
  //     data: {
  //       monitoringItems: [
  //         {
  //           id: "MON-001",
  //           parameter: "Oxygen Saturation",
  //           frequency: "continuous",
  //           thresholds: "< 92%",
  //           responsibleRole: "Nurse",
  //           duration: "During ICU stay",
  //         },
  //         {
  //           id: "MON-002",
  //           parameter: "Arterial Blood Gases",
  //           frequency: "as required",
  //           thresholds: "",
  //           responsibleRole: "ICU Team",
  //           duration: "During ICU stay",
  //         },
  //       ],
  //     },
  //     id: "M-001",
  //     createdAt: "2026-02-16T01:00:00Z",
  //   },
  //   {
  //     type: "medication",
  //     data: {
  //       medications: [
  //         {
  //           id: "MED-001",
  //           drugName: "Piperacillin–Tazobactam",
  //           dose: "4.5 g",
  //           route: "IV",
  //           frequency: "Every 8 hours",
  //           duration: "5 days",
  //           indication: "Broad-spectrum antibiotic therapy",
  //           prnInstructions: "",
  //           startDate: "2026-02-16",
  //           endDate: "2026-02-20",
  //           status: "completed",
  //           stoppedAt: "2026-02-20T00:00:00Z",
  //         },
  //         {
  //           id: "MED-002",
  //           drugName: "Methylprednisolone",
  //           dose: "60 mg",
  //           route: "IV",
  //           frequency: "Twice daily",
  //           duration: "4 days",
  //           indication: "Reduce lung inflammation",
  //           prnInstructions: "",
  //           startDate: "2026-02-16",
  //           endDate: "2026-02-19",
  //           status: "completed",
  //           stoppedAt: "2026-02-19T00:00:00Z",
  //         },
  //       ],
  //     },
  //     id: "MED-GRP-001",
  //     createdAt: "2026-02-16T02:00:00Z",
  //   },
  //   {
  //     type: "test",
  //     data: {
  //       tests: [
  //         {
  //           id: "TST-001",
  //           testName: "Chest X-Ray (Initial)",
  //           priority: "urgent",
  //           clinicalIndication: "Evaluate pneumonitis",
  //           frequency: "once",
  //           scheduledDateTime: "2026-02-16T00:00:00Z",
  //         },
  //         {
  //           id: "TST-002",
  //           testName: "ECG",
  //           priority: "routine",
  //           clinicalIndication: "Cardiac assessment",
  //           frequency: "once",
  //           scheduledDateTime: "2026-02-16T00:00:00Z",
  //         },
  //         {
  //           id: "TST-003",
  //           testName: "Echocardiogram",
  //           priority: "routine",
  //           clinicalIndication: "Cardiac function evaluation",
  //           frequency: "once",
  //           scheduledDateTime: "2026-02-17T00:00:00Z",
  //         },
  //         {
  //           id: "TST-004",
  //           testName: "Laboratory Tests",
  //           priority: "urgent",
  //           clinicalIndication: "Assess inflammatory and allergic markers",
  //           frequency: "once",
  //           scheduledDateTime: "2026-02-17T00:00:00Z",
  //         },
  //       ],
  //     },
  //     id: "T-001",
  //     createdAt: "2026-02-16T03:00:00Z",
  //   },
  //   {
  //     type: "diet",
  //     data: {
  //       dietType: "Balanced high-protein diet",
  //       restrictions: "Avoid exposure to dust and allergens",
  //       feedingMode: "oral",
  //       dieticianReferral: "",
  //     },
  //     id: "D-001",
  //     createdAt: "2026-02-17T00:00:00Z",
  //   },
  //   {
  //     type: "clinical-notes",
  //     data: {
  //       condition:
  //         "Laboratory parameters improving, respiratory status stabilizing",
  //       goals: "Continue supportive care and prepare for step-down",
  //       notes:
  //         "IgE and eosinophil counts trending down; oxygen requirement decreasing",
  //     },
  //     id: "CN-002",
  //     createdAt: "2026-02-19T00:00:00Z",
  //   },
  //   {
  //     type: "test",
  //     data: {
  //       tests: [
  //         {
  //           id: "TST-005",
  //           testName: "Repeat Laboratory Tests",
  //           priority: "routine",
  //           clinicalIndication: "Assess treatment response",
  //           frequency: "once",
  //           scheduledDateTime: "2026-02-19T00:00:00Z",
  //         },
  //         {
  //           id: "TST-006",
  //           testName: "Chest X-Ray (Repeat)",
  //           priority: "routine",
  //           clinicalIndication: "Assess resolution of pneumonitis",
  //           frequency: "once",
  //           scheduledDateTime: "2026-02-19T00:00:00Z",
  //         },
  //       ],
  //     },
  //     id: "T-002",
  //     createdAt: "2026-02-19T01:00:00Z",
  //   },],
  //   tasks: [
  //     {
  //       id: makeId(),
  //       taskTitle: "Nebulization therapy",
  //       description: "Bronchodilator nebulization sessions",
  //       priority: "High",
  //       dueDate: "2026-02-16",
  //       dueTime: "",
  //       assignedTo: "Nurse",
  //       category: "Rounds",
  //       status: "Completed",
  //       createdAt: "2026-02-16T00:00:00Z",
  //       recurrencePattern: {
  //         isRecurring: true,
  //         frequency: "daily",
  //         interval: 1,
  //         specificTimes: [],
  //         daysOfWeek: [],
  //         dayOfMonth: null,
  //         startDateTime: "2026-02-16T00:00:00Z",
  //         endCondition: { type: "untilDate", date: "2026-02-21", duration: { value: null, unit: "days" } },
  //       },
  //     },
  //   ],
  // },
  {
    id: "4",
    name: "Anita Desai",
    wardNo: "B2",
    roomNo: "202",
    bedNo: "4B",
    type: "Main",
    condition: "Cardiac monitoring post MI",
    diseases: ["Acute Myocardial Infarction", "Hyperlipidemia"],
    age: 58,
    gender: "Female",
    admissionDate: "2026-01-08",
    vitals: {
      bloodPressure: "110/70",
      heartRate: 68,
      temperature: 98.2,
      oxygenSaturation: 97,
      respiratoryRate: 14,
      weight: 62,
      lastUpdated: "2026-01-09 08:15",
    },
    carePlans: [],
    referredDoctors: [
      { id: "doc1", name: "Dr. Arjun Mehta", specialty: "Endocrinology", initials: "AM", isPrimary: true },
      { id: "doc2", name: "Dr. Kavya Nair", specialty: "Internal Medicine", initials: "KN", isPrimary: false },
      { id: "doc3", name: "Dr. Rohan Gupta", specialty: "Nephrology", initials: "RG", isPrimary: false }
    ],
    tasks: (() => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      return [
        {
          id: makeId(),
          taskTitle: "Blood Test - CBC",
          description: "Collect blood sample for Complete Blood Count",
          priority: "Medium",
          category: "Labs",
          status: "Pending",
          assignedTo: "Nurse",
          createdAt: new Date().toISOString(),
          recurrencePattern: {
            isRecurring: true,
            frequency: "daily",
            interval: 1,
            specificTimes: ["07:00"],
            daysOfWeek: [],
            dayOfMonth: null,
            startDateTime: `${yesterdayStr}T07:00:00Z`,
            endCondition: {
              type: "duration",
              duration: {
                value: 3,
                unit: "days"
              }
            },
            timezone: "Asia/Kolkata"
          }
        }
      ];
    })(),
  },
  {
    id: "5",
    name: "Suresh Patel",
    wardNo: "C3",
    roomNo: "301",
    bedNo: "5A",
    type: "Referral",
    condition: "Acute kidney injury requiring dialysis",
    diseases: ["Chronic Kidney Disease Stage 4", "Diabetes"],
    age: 61,
    gender: "Male",
    admissionDate: "2026-01-06",
    vitals: {
      bloodPressure: "150/95",
      heartRate: 78,
      temperature: 98.4,
      oxygenSaturation: 95,
      respiratoryRate: 18,
      weight: 80,
      lastUpdated: "2026-01-09 09:30",
    },
    carePlans: [],
    referredDoctors: [
      { id: "doc3", name: "Dr. Rohan Gupta", specialty: "Nephrology", initials: "RG", isPrimary: true }
    ],
    tasks: [],
  },
  {
    id: "6",
    name: "Lakshmi Iyer",
    wardNo: "C3",
    roomNo: "302",
    bedNo: "6B",
    type: "Main",
    condition: "Post hip replacement rehabilitation",
    diseases: ["Osteoarthritis"],
    age: 72,
    gender: "Female",
    admissionDate: "2026-01-03",
    vitals: {
      bloodPressure: "125/75",
      heartRate: 70,
      temperature: 98.0,
      oxygenSaturation: 99,
      respiratoryRate: 15,
      weight: 55,
      lastUpdated: "2026-01-09 08:00",
    },
    carePlans: [],
    referredDoctors: [
      { id: "doc4", name: "Dr. Priya Sharma", specialty: "Cardiology", initials: "PS", isPrimary: true }
    ],
    tasks: [
      {
        id: makeId(),
        taskTitle: "Dressing Change",
        description: "Change surgical dressing and check wound",
        priority: "Medium",
        category: "Rounds",
        status: "Pending",
        assignedTo: "Nurse",
        createdAt: new Date().toISOString(),
        recurrencePattern: {
          isRecurring: true,
          frequency: "weekly",
          interval: 1,
          specificTimes: ["10:00"],
          daysOfWeek: ["monday", "thursday"],
          dayOfMonth: null,
          startDateTime: "2026-02-10T10:00:00Z",
          endCondition: {
            type: "duration",
            duration: {
              value: 2,
              unit: "weeks"
            }
          },
          timezone: "Asia/Kolkata"
        }
      }
    ],
  },
  {
    id: "7",
    name: "Vikram Singh",
    wardNo: "A1",
    roomNo: "103",
    bedNo: "7A",
    type: "Main",
    condition: "Acute pancreatitis - NPO with IV fluids",
    diseases: ["Acute Pancreatitis", "Gallstones"],
    age: 48,
    gender: "Male",
    admissionDate: "2026-01-07",
    vitals: {
      bloodPressure: "118/78",
      heartRate: 85,
      temperature: 99.8,
      oxygenSaturation: 97,
      respiratoryRate: 17,
      weight: 82,
      lastUpdated: "2026-01-09 09:15",
    },
    carePlans: [],
    referredDoctors: [
      { id: "doc5", name: "Dr. Suresh Patel", specialty: "Neurology", initials: "SP", isPrimary: true }
    ],
    tasks: [],
  },
  {
    id: "8",
    name: "Meera Reddy",
    wardNo: "B2",
    roomNo: "203",
    bedNo: "8B",
    type: "Referral",
    condition: "Stroke recovery with physiotherapy",
    diseases: ["Ischemic Stroke", "Atrial Fibrillation"],
    age: 65,
    gender: "Female",
    admissionDate: "2026-01-02",
    vitals: {
      bloodPressure: "135/85",
      heartRate: 76,
      temperature: 98.4,
      oxygenSaturation: 96,
      respiratoryRate: 16,
      weight: 58,
      lastUpdated: "2026-01-09 07:30",
    },
    carePlans: [],
    referredDoctors: [
      { id: "doc2", name: "Dr. Kavya Nair", specialty: "Internal Medicine", initials: "KN", isPrimary: true }
    ],
    tasks: [],
  },
];

// In-memory storage to simulate database
let patientsDb = [...mockPatients];

// Store completed task instance IDs (since tasks are generated on-the-fly)
// Format: { "taskTemplateId-scheduledDateTime": true }
let completedTaskInstances = {};

export const patientApi = {
  // GET /api/patients
  async getPatients() {
    await delay(300);
    return { data: clone(patientsDb), status: 200 };
  },

  // GET /api/patients/:id
  async getPatientById(id) {
    await delay(200);
    const patient = patientsDb.find((p) => p.id === id);
    if (!patient) {
      return { data: null, status: 404, error: "Patient not found" };
    }
    return { data: clone(patient), status: 200 };
  },

  // GET /api/patients/search?q=query
  async searchPatients(query) {
    await delay(200);
    const lowercaseQuery = query.toLowerCase();
    const results = patientsDb.filter(
      (p) =>
        p.name.toLowerCase().includes(lowercaseQuery) ||
        p.wardNo.toLowerCase().includes(lowercaseQuery) ||
        p.roomNo.toLowerCase().includes(lowercaseQuery) ||
        p.bedNo.toLowerCase().includes(lowercaseQuery) ||
        p.condition.toLowerCase().includes(lowercaseQuery)
    );
    return { data: clone(results), status: 200 };
  },

  // POST /api/patients/:id/care-plans
  async addCarePlan(patientId, carePlan) {
    await delay(300);
    const patientIndex = patientsDb.findIndex((p) => p.id === patientId);
    if (patientIndex === -1) {
      return { data: null, status: 404, error: "Patient not found" };
    }

    let normalizedCarePlan = carePlan;
    if (carePlan?.type === "medication") {
      normalizedCarePlan = {
        ...carePlan,
        data: normalizeMedicationCarePlanData(carePlan.data),
      };
    } else if (carePlan?.type === "test") {
      normalizedCarePlan = {
        ...carePlan,
        data: normalizeTestCarePlanData(carePlan.data),
      };
    } else if (carePlan?.type === "monitoring") {
      normalizedCarePlan = {
        ...carePlan,
        data: normalizeMonitoringCarePlanData(carePlan.data),
      };
    }

    const newCarePlan = {
      ...normalizedCarePlan,
      id: makeId(),
      createdAt: nowIso(),
    };

    patientsDb[patientIndex].carePlans.push(newCarePlan);
    console.log("carePlans", patientsDb[patientIndex]);

    return { data: clone(newCarePlan), status: 201 };
  },

  // PUT /api/patients/:id/care-plans/:carePlanId/medications/:medicationId/stop
  async stopMedication(patientId, carePlanId, medicationId) {
    await delay(200);
    const patientIndex = patientsDb.findIndex((p) => p.id === patientId);
    if (patientIndex === -1) {
      return { data: null, status: 404, error: "Patient not found" };
    }

    const planIndex = patientsDb[patientIndex].carePlans.findIndex(
      (cp) => cp.id === carePlanId
    );
    if (planIndex === -1) {
      return { data: null, status: 404, error: "Care plan not found" };
    }

    const plan = patientsDb[patientIndex].carePlans[planIndex];
    if (plan.type !== "medication") {
      return { data: null, status: 400, error: "Not a medication care plan" };
    }

    plan.data = normalizeMedicationCarePlanData(plan.data);
    const medIndex = plan.data.medications.findIndex((m) => m.id === medicationId);
    if (medIndex === -1) {
      return { data: null, status: 404, error: "Medication not found" };
    }

    const existing = plan.data.medications[medIndex];
    if (existing.status === "stopped") {
      return { data: clone(plan), status: 200 };
    }

    const stoppedAt = nowIso();
    plan.data.medications[medIndex] = {
      ...existing,
      status: "stopped",
      stoppedAt,
      endDate: existing.endDate || stoppedAt.slice(0, 10), // yyyy-mm-dd for date input style
    };

    return { data: clone(plan), status: 200 };
  },

  // GET /api/patients/:id/care-plans
  async getCarePlans(patientId) {
    await delay(200);
    const patient = patientsDb.find((p) => p.id === patientId);
    if (!patient) {
      return { data: null, status: 404, error: "Patient not found" };
    }
    return { data: clone(patient.carePlans), status: 200 };
  },

  // PUT /api/patients/:id/vitals
  async updateVitals(patientId, vitals) {
    await delay(200);
    const patientIndex = patientsDb.findIndex((p) => p.id === patientId);
    if (patientIndex === -1) {
      return { data: null, status: 404, error: "Patient not found" };
    }

    patientsDb[patientIndex].vitals = {
      ...vitals,
      lastUpdated: new Date().toLocaleString(),
    };

    return { data: clone(patientsDb[patientIndex].vitals), status: 200 };
  },

  // GET /api/patients/:id/tasks
  async getTasks(patientId) {
    await delay(200);
    const patient = patientsDb.find((p) => p.id === patientId);
    if (!patient) {
      return { data: null, status: 404, error: "Patient not found" };
    }
    return { data: clone(patient.tasks || []), status: 200 };
  },

  // PUT /api/patients/:id/tasks
  async saveTasks(patientId, tasks) {
    await delay(200);
    const patientIndex = patientsDb.findIndex((p) => p.id === patientId);
    if (patientIndex === -1) {
      return { data: null, status: 404, error: "Patient not found" };
    }

    patientsDb[patientIndex].tasks = clone(Array.isArray(tasks) ? tasks : []);

    return { data: clone(patientsDb[patientIndex].tasks), status: 200 };
  },

  // GET /api/nurse/tasks - Get all generated tasks for nurses across all patients
  async getNurseTasks() {
    await delay(200);
    const allTasks = [];
    patientsDb.forEach((patient) => {
      if (patient.tasks && Array.isArray(patient.tasks)) {
        patient.tasks.forEach((taskTemplate) => {
          if (taskTemplate.recurrencePattern?.isRecurring) {
            // Generate instances from recurring tasks
            const generatedTasks = generateTasksFromRecurrence(
              {
                ...taskTemplate,
                patientId: patient.id,
                patientName: patient.name,
                roomNo: patient.roomNo,
                wardNo: patient.wardNo,
              },
              taskTemplate.recurrencePattern,
              true // Check completed status
            );
            allTasks.push(...generatedTasks);
          } else {
            // Add non-recurring tasks directly
            allTasks.push({
              ...taskTemplate,
              id: taskTemplate.id,
              templateId: taskTemplate.id,
              patientId: patient.id,
              patientName: patient.name,
              roomNo: patient.roomNo,
              wardNo: patient.wardNo,
              dueDate: taskTemplate.dueDate || taskTemplate.recurrencePattern?.startDateTime?.split('T')[0] || '',
              dueTime: taskTemplate.dueTime || taskTemplate.recurrencePattern?.startDateTime?.split('T')[1]?.slice(0, 5) || '',
              scheduledDateTime: taskTemplate.recurrencePattern?.startDateTime || (taskTemplate.dueDate && taskTemplate.dueTime
                ? `${taskTemplate.dueDate}T${taskTemplate.dueTime}:00Z`
                : taskTemplate.dueDate
                  ? `${taskTemplate.dueDate}T00:00:00Z`
                  : new Date().toISOString()),
            });
          }
        });
      }
    });
    return { data: clone(allTasks), status: 200 };
  },

  // GET /api/patients/:id/nurse-tasks - Get generated tasks for a specific patient
  async getPatientNurseTasks(patientId) {
    await delay(200);
    const patient = patientsDb.find((p) => p.id === patientId);
    if (!patient) {
      return { data: null, status: 404, error: "Patient not found" };
    }
    const allTasks = [];
    if (patient.tasks && Array.isArray(patient.tasks)) {
      patient.tasks.forEach((taskTemplate) => {
        if (taskTemplate.recurrencePattern?.isRecurring) {
          // Generate instances from recurring tasks
          const generatedTasks = generateTasksFromRecurrence(
            {
              ...taskTemplate,
              patientId: patient.id,
              patientName: patient.name,
              roomNo: patient.roomNo,
              wardNo: patient.wardNo,
            },
            taskTemplate.recurrencePattern,
            true // Check completed status
          );
          allTasks.push(...generatedTasks);
        } else {
          // Add non-recurring tasks directly
          allTasks.push({
            ...taskTemplate,
            id: taskTemplate.id,
            templateId: taskTemplate.id,
            patientId: patient.id,
            patientName: patient.name,
            roomNo: patient.roomNo,
            wardNo: patient.wardNo,
            dueDate: taskTemplate.dueDate || taskTemplate.recurrencePattern?.startDateTime?.split('T')[0] || '',
            dueTime: taskTemplate.dueTime || taskTemplate.recurrencePattern?.startDateTime?.split('T')[1]?.slice(0, 5) || '',
            scheduledDateTime: taskTemplate.recurrencePattern?.startDateTime || (taskTemplate.dueDate && taskTemplate.dueTime
              ? `${taskTemplate.dueDate}T${taskTemplate.dueTime}:00Z`
              : taskTemplate.dueDate
                ? `${taskTemplate.dueDate}T00:00:00Z`
                : new Date().toISOString()),
          });
        }
      });
    }
    return { data: clone(allTasks), status: 200 };
  },

  // PUT /api/nurse/tasks/:taskId - Update a single generated task instance status
  async updateTaskStatus(taskTemplateId, status, scheduledDateTime) {
    await delay(200);
    if (taskTemplateId == null || scheduledDateTime == null) {
      return { data: { templateId: taskTemplateId, status, scheduledDateTime }, status: 400 };
    }
    // Store completed task instance using template ID and scheduled date time
    const instanceKey = `${taskTemplateId}-${scheduledDateTime}`;
    if (status === "Completed") {
      completedTaskInstances[instanceKey] = true;
    } else {
      delete completedTaskInstances[instanceKey];
    }
    return { data: { templateId: taskTemplateId, status, scheduledDateTime }, status: 200 };
  },

  // Check if a task instance is completed
  isTaskInstanceCompleted(taskTemplateId, scheduledDateTime) {
    return completedTaskInstances[`${taskTemplateId}-${scheduledDateTime}`] || false;
  },
};

// Exports for use with IPD Context (so data can live in React state and persist across navigation)
export {
  mockPatients,
  clone,
  makeId,
  nowIso,
  normalizeMedicationCarePlanData,
  normalizeTestCarePlanData,
  normalizeMonitoringCarePlanData,
  generateTasksFromRecurrence,
  mockAvailableDoctors,
};
